<template>
    <label :for="'select-'+name" :class="[{'has-error': !!error}]">
        <span class="label" :class="labelClassesToApply" v-if="!!label && !$slots.label">{{label}}</span>
        <slot name="label"/>
        <div class="select-wrapper" :class="{open , empty}" v-focus-outside="closeOptions" v-click-outside="closeOptions">
            <input v-if="searchable" 
                :class="[inputClassesToApply]" 
                type="text" 
                :value="search" 
                ref="input"
                @click="openOptions($event)" 
                @input="searchOptions($event.target.value)" 
                @keydown.enter="open ? selectCurrent() : openOptions()"
                @keydown.esc="closeOptions(); $refs.input.focus();"
                @keydown.down="next($event)"
                @keydown.up="previous($event)"
                @focusout="removeHovering()"
                :placeholder="display" />
            <div v-else tabindex="0" 
                :id="'select-'+name" 
                class="bbox-select"
                :class="[inputClassesToApply]" 
                ref="select"
                @keydown.down="next($event)"
                @keydown.esc="closeOptions(); $refs.select.focus();"
                @keydown.up="previous($event)"
                @keydown.enter="open ? selectCurrent() : openOptions()"
                @click="toggleOptions($event)">
                {{ display }}
            </div>
            <ul class="options border border-white-darker-3" ref="options" >
                <div class="wrapper">
                    <li v-for="(option, index) in showOptions"
                        :class="[{selected: option===value, [`${optionHoverClasses}`]: index===currentHover, [`${optionClasses}`]: index!==currentHover}]" 
                        :key="index" @click="select(option)"
                        :ref="'option-'+index"
                        @keyup.enter="select(option)"
                        @keyup.esc="closeOptions(); $event.target.blur();"
                        @focus="$event.target.classList.remove('hovering')">
                        {{typeof option === "object" ? option.name : option}}
                        <!-- <span v-if="option===value" style="font-size: 80%" class="icon-right"><Icon name="icon-check"/></span> -->
                    </li>
                </div>
            </ul>
            <span class="input-error" v-if="!!error">{{ error }}</span>
        </div>
        <input type="hidden" :name="name" :value="value">
    </label>
</template>

<script>
export default {
    name: 'BSelect',
    props: {
        /**
         * Classes para estilizar o label
          */
        labelClasses: {
          type: String,
          default: "display-block margin-bottom-tiny ink-base"
        },
        /**
         * Classes para estilizar o select
          */
        inputClasses: {
          type: String,
          default: "ink-base border border-white-darker-3 bg-white-darker-2 padding-normal padding font-size-big border-focus-ink-lighter-2"
        },
        /**
         * Classes para estilizar as opções do select
          */
        optionClasses: {
          type: String,
          default: "border-top border-bottom border-white-darker-3 padding-normal font-size-big bg-white-base bg-hover-blue-lighter-4"
        },
        /**
         * Classes para estilizar o hover das opções do select
          */
        optionHoverClasses: {
          type: String,
          default: "border-top border-bottom border-white-darker-3 padding-normal font-size-big bg-blue-lighter-4"
        },
        /**
         * Classes para estilizar o label quando há uma mensagem de erro
          */
        labelErrorClasses: {
          type: String,
          default: "display-block margin-bottom-tiny ink-base"
        },
        /**
         * Classes para estilizar o select quando há uma mensagem de erro
          */
        inputErrorClasses: {
          type: String,
          default: "ink-base border border-red-lighter-2 border-focus-red-base  bg-red-lighter-4 bg-focus-red-lighter-3 padding-normal padding font-size-big "
        },
        /**
         * Valores das opções do select, pode ser tanto um array de Strings, como um array de objetos, ex: `[{name:'nome', value:'valor'}]`
          */
        options: Array,
        /**
         * Name padrão do select
          */
        name: {
            type: String,
            required: true
        },
        /**
         * Valor exibido como placeholder do select
          */
        placeholder: {
            type: String,
            default: 'Selecione...',
            label: String
        },
        /**
         * Texto do label do select
          */
        label: String,
        /**
         * Mensagem de erro
          */
        error: {
            type: String,
            default: ''
        },
        /**
         * Valor selecionado no select 
         * @model
          */
        value: {
            default: ''
        },
        /**
         * Se true as opções do select podem ser filtradas
          */
        searchable: Boolean
    },
    directives: {
        'click-outside': {
            bind: function (el, binding, vnode) {
                el.clickOutsideEvent = function (event) {
                // here I check that click was outside the el and his childrens
                if (!(el == event.target || el.contains(event.target))) {
                    // and if it did, call method provided in attribute value
                    vnode.context[binding.expression](event);
                }
                };
                document.body.addEventListener('click', el.clickOutsideEvent)
            },
            unbind: function (el) {
                document.body.removeEventListener('click', el.clickOutsideEvent)
            },
        },
        'focus-outside': {
            bind: function (el, binding, vnode) {
                el.focusOutsideEvent = function (event) {
                // here I check that focus was outside the el and his childrens
                if (!(el == event.target || el.contains(event.target))) {
                    // and if it did, call method provided in attribute value
                    vnode.context[binding.expression](event);
                }
                };
                document.addEventListener('focusin', el.focusOutsideEvent)
            },
            unbind: function (el) {
                document.removeEventListener('focusin', el.focusOutsideEvent)
            },
        }
    },
    data() {
        return {
            showOptions: this.options,
            search: this.placeholder,
            open: false,
            currentHover: 0,
            val: this.value
        }
    },
    computed: {
        empty() {
            return (typeof this.value === "object" ? ( this.value.name ? false : true ) : (this.value ? false : true))
        },
        display() {
            return typeof this.val === 'object' ? (this.val.name ? this.val.name : this.placeholder) : (this.val ? this.val : this.placeholder)
        },
        inputClassesToApply() {
          if(this.error) return this.inputErrorClasses;
          else return this.inputClasses;
        },
        labelClassesToApply() {
          if(this.error) return this.labelErrorClasses;
          else return this.labelClasses;
        }
    },
    updated() {
        var firstListItem = this.$refs.options.querySelectorAll('li')[0];
        if(this.open && this.showOptions.length && this.search) {
            firstListItem.classList.add('hovering');
            this.currenHover = 0;
        } else if(this.search=='' && this.open) {
            firstListItem.classList.remove('hovering');
        }
    },
    methods: {
        searchOptions(val) {
            if(!this.open) this.open = true;
            if(val==='') {
                this.search = val;
                this.showOptions = this.options;
            } else {
                let original = val;
                val = val.toString().toLowerCase();
                this.showOptions = this.options.filter((option) => {
                    if(typeof option === "object") return option.name.toString().toLowerCase().includes(val) || option.value.toString().toLowerCase().includes(val);
                    return option.toString().toLowerCase().includes(val);
                });
                this.search = original;
                this.currentHover = 0;
            }
        },
        selectFirst() {
            if(this.showOptions.length > 0) {
                this.select(this.showOptions[0]);
                //if(!!$event) $event.target.blur();
            }
        },
        selectCurrent() {
            this.select(this.showOptions[this.currentHover]);  
        },
        closeOptions() {
            this.search = this.display;
            this.open = false;
        },
        openOptions() {
            if(this.searchable) this.$refs.input.focus();
            else this.$refs.select.focus();
            this.search = '';
            this.showOptions = this.options;
            this.currentHover = 0;
            this.open = true;
        },
        toggleOptions(event) {
            if(this.open) {
                this.closeOptions();
            } else {
                this.openOptions(event);
            }
        },
        select(option) {
            this.search = (typeof option === "object" ? option.name : option);
            this.showOptions = this.options;
            /**
             * Evento emitido quando usuário troca o valor selecionado do select
             * @property {option}
             * @type {change}
              */
            this.$emit('change', option);
            /**
             * Evento emitido quando usuário troca o valor selecionado do select
             * @property {option}
             * @type {input}
              */
            this.$emit('input', option);
            this.val = option;
            this.open = false;
        },
        removeHovering() {
            let hovering = this.$refs.options.querySelectorAll('li.hovering')[0];
            if(hovering) {
                hovering.classList.remove('hovering');
                hovering.setAttribute('tabindex', '0');
            }
        },
        next(e) {
            e.preventDefault();
            if(this.currentHover+1 < this.showOptions.length) {
                this.currentHover++;
                this.$refs['option-'+this.currentHover][0].scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'start'});
            }
        },
        previous(e) {
            e.preventDefault();
            if(this.currentHover-1 >= 0) {
                this.currentHover--;
                var target = this.$refs['option-'+this.currentHover][0].scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'start'});
            }
        }
    },
    mounted() {
        var width = this.$refs.options.scrollWidth;
        this.$refs.select.style.minWidth = width;
        this.open = false;
    }
}
</script>

<style scoped lang="scss">

    label, label * {
        box-sizing: border-box;
    }
    .select-wrapper {
        position: relative;
        width: 100%;
        display: inline-block; //to counter margin merging
        line-height: 1;
        &.empty input, &.empty .bbox-select {
            color: #8F8A9B;
        }
    }
    .has-error .select-wrapper, .has-error .select-wrapper {
        margin-bottom: 0;
    }
    .has-error .select-wrapper.open {
        margin-bottom: calc(1.18rem + 0.5em);
    }
    ul.options {
        position: absolute;
        display: none;
        top: calc(100% + 8px);
        left: 0;
        min-width: 100%;
        height: auto;
        max-height: 300px;
        margin-bottom: 0;
        padding-left: 0;
        overflow: hidden;
        cursor: pointer;
        z-index: 3;
        margin-top: 0;
        border-radius: 5px;
        div.wrapper {
            overflow-y: auto;
            overflow-x: hidden;
            max-height: 300px;
        }
        li {
            display: block;
            width: 100%;
            line-height: 1.33;
            position: relative;
            z-index: 1;
            border-top-color: transparent;
        }
        li:last-child {
            border-bottom-left-radius: 5px;
            border-bottom-right-radius: 5px;
        }
        li:active, li:hover, li:focus, li.hovering {
            outline: none;
        }
        li.selected {
            /* padding-right: calc(2.025rem + 0.8em); */
            position: relative;
        }
    }
    .select-wrapper.open ul.options {
        display: block;
    }
    input, .bbox-select {
        border-radius: 5px;
        /* padding: 0.8125rem 1.3125rem; */
        margin: 0;
        display: block;
        width: 100%;
        position: relative;
        z-index: 1;
        outline: none;
        cursor: pointer;
    }
    span.input-error {
        display: block;
        text-align: right;
        color: #F95E5A;
        font-size: 1.125rem;
        margin-top: 0.5rem;
        line-height: 1;
    }
    .has-error .open span.input-error {
        display: none;
    }
    span.icon-left {
        position: absolute;
        left: 1.3125rem;
        top: calc(0.8125rem + 0.05em);
        color: #8F8A9B;
        z-index: 2;
        cursor: pointer;
    }
    span.icon-right {
        position: absolute;
        top: calc(0.8125rem + 0.05em);
        right: 1.3125rem;
        color: #8F8A9B;
        z-index: 2;
        cursor: pointer;
    }
</style>

<docs>
    Caixa de seleção

    As importações dos arquivos abaixo são necessárias para o funcionamento do componente:
    - position.scss
    - spacing.scss
    - colors-classes.scss
    - typography.scss

    ```js
    <BSelect name="example"
    label="Label" :options="['Opção 1', 'Opção 2', 'Opção 3', 'Opção 4']"
    error="Mensangem de erro!"></BSelect>
    ```
</docs>